import { useCallback, useState } from 'react';
import { useTRPCMutation } from '../../trpc/trpc';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';
import { useApplyFreshUserToken } from './useApplyFreshUserToken';
import { clearInviteToken } from './teamInviteToken';
import { useJoinedToast } from './useJoinedToast';

/** A used invite: the session token `team.accept` returned and the names the c8 toast shows. */
export interface JoinedWorkspace {
  token: string;
  workspaceName: string;
  homeName: string | null;
}

/**
 * design: workspaces c8. After `team.accept`: apply the fresh token, drop the stored invite token,
 * show the toast and open Home. A failed token swap can be retried without a second `team.accept`.
 */
export function useFinishJoin() {
  const applyToken = useApplyFreshUserToken();
  const router = useSMLRouter();
  const showJoinedToast = useJoinedToast();
  // Set once `team.accept` answers. From then on the invite is used: the preview answers
  // M24_TEAM_INVITE_INVALID once the fresh token refetches it, and the page must not show that card.
  const [joined, setJoined] = useState<JoinedWorkspace | null>(null);
  // Set only after the fresh token is applied. If that fails, the page offers a retry.
  const [hasJoined, setHasJoined] = useState(false);

  const mutation = useTRPCMutation({
    mutationFn: (workspace: JoinedWorkspace) => applyToken(workspace.token),
    onSuccess: (_data: void, workspace: JoinedWorkspace) => {
      // Drop the stored token only once the session is in the new workspace, so a failed switch
      // can still be retried from this page.
      clearInviteToken();
      setHasJoined(true);
      showJoinedToast(workspace.workspaceName, workspace.homeName);
      router.replace(SMLRoutes.home);
    },
  });

  const { mutate } = mutation;
  const finish = useCallback(
    (workspace: JoinedWorkspace) => {
      setJoined(workspace);
      mutate(workspace);
    },
    [mutate]
  );
  const retry = useCallback(() => {
    if (joined !== null) {
      mutate(joined);
    }
  }, [joined, mutate]);

  return { finish, retry, joined, hasJoined, error: joined !== null ? mutation.error : null };
}
